(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.RwadyCollectionSearchStateApi = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  const DISCOUNT_PARAM = 'filter.p.m.custom.sale_filter';
  const VALID_SCOPES = new Set(['collection', 'all']);

  const clonePairs = (pairs) => (pairs || []).map(([key, value]) => [String(key), String(value)]);

  const stablePairs = (pairs) => clonePairs(pairs).sort(([leftKey, leftValue], [rightKey, rightValue]) => {
    const keyOrder = leftKey.localeCompare(rightKey);
    return keyOrder || leftValue.localeCompare(rightValue);
  });

  const normalizeState = (state = {}) => ({
    scope: VALID_SCOPES.has(state.scope) ? state.scope : 'collection',
    q: String(state.q || '').trim(),
    filters: stablePairs(state.filters),
    sort: String(state.sort || ''),
    page: Math.max(1, Number.parseInt(state.page, 10) || 1),
    discount: Boolean(state.discount),
  });

  const fromParams = (input, defaults = {}) => {
    const params = input instanceof URLSearchParams ? input : new URLSearchParams(input || '');
    const filters = [];
    let discount = params.get('rwady_discount') === '1';

    params.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      if (key === DISCOUNT_PARAM) {
        discount = true;
        return;
      }
      filters.push([key, value]);
    });

    return normalizeState({
      scope: params.get('rwady_scope') || defaults.scope,
      q: params.get('rwady_q') ?? params.get('q') ?? defaults.q,
      filters,
      sort: params.get('sort_by') ?? defaults.sort,
      page: params.get('page') ?? defaults.page,
      discount,
    });
  };

  const fromForm = (formParams, currentState) => {
    const params = formParams instanceof URLSearchParams ? formParams : new URLSearchParams(formParams || '');
    const next = fromParams(params, currentState);
    next.scope = currentState.scope;
    next.q = currentState.q;
    next.page = 1;
    return normalizeState(next);
  };

  const publicParams = (state, normalizedQuery) => {
    const normalized = normalizeState(state);
    const params = new URLSearchParams();
    normalized.filters.forEach(([key, value]) => params.append(key, value));
    if (normalized.sort) params.set('sort_by', normalized.sort);
    if (normalized.discount) params.set('rwady_discount', '1');
    params.set('rwady_scope', normalized.scope);
    if (normalized.q) {
      params.set('q', String(normalizedQuery || normalized.q));
      params.set('rwady_q', normalized.q);
    }
    if (normalized.page > 1) params.set('page', String(normalized.page));
    return params;
  };

  const request = (state, options = {}) => {
    const normalized = normalizeState(state);
    const params = new URLSearchParams();
    normalized.filters.forEach(([key, value]) => params.append(key, value));
    if (normalized.sort) params.set('sort_by', normalized.sort);
    if (normalized.page > 1) params.set('page', String(normalized.page));
    if (normalized.discount && options.discountValue) {
      params.set(DISCOUNT_PARAM, options.discountValue);
    }

    const rootPath = String(options.root || '/').replace(/\/?$/, '/');
    if (normalized.scope === 'all' && normalized.q) {
      params.set('q', String(options.normalizedQuery || normalized.q));
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      return { source: 'search', url: `${rootPath}search?${params}` };
    }

    params.set('section_id', options.sectionId || '');
    const path = normalized.scope === 'all'
      ? `${rootPath}collections/all`
      : String(options.collectionPath || '/collections/all');
    return { source: normalized.scope === 'all' ? 'all-products' : 'collection', url: `${path}?${params}` };
  };

  const cacheKey = (state, normalizedQuery) => {
    const normalized = normalizeState(state);
    return JSON.stringify({
      scope: normalized.scope,
      q: String(normalizedQuery || normalized.q).toLocaleLowerCase(),
      filters: stablePairs(normalized.filters),
      sort: normalized.sort,
      page: normalized.page,
      discount: normalized.discount,
    });
  };

  return {
    DISCOUNT_PARAM,
    normalizeState,
    fromParams,
    fromForm,
    publicParams,
    request,
    cacheKey,
  };
});
